import { useBearingsTableStore } from '@hub/bearing-hub/BearingHubPage/BearingsTable/useBearingsTableStore';
import { lifetimeConfigParams, spatialTypeOfMovement } from '@hub/bearing-hub/common/constants';
import { IbhEventCategory, TrackedIbhEventActions } from '@hub/bearing-hub/common/tracking.config';
import type {
  LifetimeConfigTypeOfOperation,
  LifetimeParameters,
  SpatialTypeOfMovement,
  TemporalTypeOfMovement,
} from '@hub/bearing-hub/common/types';
import { lifetimeConfigTypeOfOperation } from '@hub/bearing-hub/common/types';
import { useLifetimeParametersStore } from '@hub/bearing-hub/libs/BearingsLifetime/useIbhLifetimeParametersValuesStore';
import { FilterSelectAutocomplete } from '@hub/common/inputs/FilterSelectAutocomplete';
import { ButtonGroup } from '@hub/common/ButtonGroup/ButtonGroup';
import { Button, TYPE } from '@hub/common/Button/Button';
import { Icon, IconColor, IconID } from '@hub/common/Icon/Icon';
import { SortingState } from '@hub/libs/bearing-hub.helper';
import { getLocale } from '@hub/libs/constants';
import { scrollToElement } from '@hub/libs/utils';
import { usePiwikPro } from '@piwikpro/next-piwik-pro';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { FilterResetLink } from '@hub/common/FilterResetLink/FilterResetLink';
import type { OppositeMaterialKeyType } from '../LifeTimePopUpInfo';
import { OppositeMaterialMap } from '../LifeTimePopUpInfo';
import type { IbhShape } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { defaultLifetimeFormConfig as trsConfig } from '@igus/tribology-domain';
import { useDimensionsStore } from '@hub/hooks/states/useIbhDimensionsValuesStore';
import type { LifetimeInputFieldConfig } from '@igus/tribology-domain/src/lib/lifetime/lifetime-calculator-definitions';
import { Modal, ModalVariant } from '@hub/common/Modal/Modal';
import { BasicNumberInput } from '@hub/common/BasicNumberInput/BasicNumberInput';
import { Switch } from '@hub/bearing-hub/common/Switch/Switch';
import {
  createInputsDefinitions,
  LifetimeParameterInputSectionType,
  LifetimeParameterInputType,
} from './inputsDefinitions';
import { useInputValidation } from './useInputValidation';
import { useLifetimeForm } from './useLifetimeForm';
import { round4, UnitSystem } from '@igus/kopla-domain';
import { createNumberFormatter } from '@icommerce/i18n';

interface LifetimeParameterFormProps {
  isEmbedded?: boolean;
  onCalculate: () => void;
  onReset: () => void;
  onFormUnitSystemChange?: (unitSystem: UnitSystem, lifetimeParameters: LifetimeParameters) => void;
  shapeKey: IbhShape;
  unitSystem: UnitSystem;
  lifetimeIsLoading?: boolean;
}

export const LifetimeParametersForm = ({
  onCalculate,
  onReset,
  onFormUnitSystemChange,
  isEmbedded,
  shapeKey,
  unitSystem,
  lifetimeIsLoading,
}: LifetimeParameterFormProps) => {
  const { t } = useTranslation(['common']);
  const {
    lifetimeParameters,
    formUnitSystem,
    setLifetimeParametersValue,
    hasLifetimeParameterValues,
    setFormUnitSystem,
  } = useLifetimeParametersStore();
  const { setTableHeadersSortingState } = useBearingsTableStore();
  const { validateInput } = useInputValidation({ formUnitSystem });
  const dimension = useDimensionsStore(state => state.dimensions[shapeKey]);
  const { CustomEvent } = usePiwikPro();
  const router = useRouter();
  const locale = getLocale(router);
  const numberFormatter = useMemo(
    () => createNumberFormatter(locale, { minimumFractionDigits: 2, maximumFractionDigits: 4 }),
    [locale]
  );

  const innerDiameter = useMemo(() => dimension?.d1, [dimension, shapeKey]);
  const outerDiameter = useMemo(() => dimension?.d2, [dimension, shapeKey]);
  const bearingLength: number | undefined = useMemo(() => {
    if (dimension && 'b1' in dimension) {
      return dimension.b1;
    }
    return undefined;
  }, [dimension, shapeKey]);

  const {
    onLifetimeParameterValueChange,
    getSurfacePressureValueForDefinitions,
    getRotatingSpeedValue,
    getLinearSpeedValue,
    getPivotingSpeedValue,
    visibilityMap,
    ROUNDING_PRECISION,
  } = useLifetimeForm({
    shapeKey,
    dimensions: {
      innerDiameter,
      outerDiameter,
      bearingLength,
    },
    formUnitSystem,
    unitSystem,
    lifetimeParameters,
    setLifetimeParametersValue,
  });

  const [isFormValid, setFormValidation] = useState(true);
  const [hintMessage, setHintMessage] = useState<{ message: string; title: string; image?: string } | undefined>(
    undefined
  );

  const handleFormUnitSystemChange = (isImperial: boolean) => {
    const nextFormUnitSystem = isImperial ? UnitSystem.imperial : UnitSystem.metric;
    const convertedLifetimeParameters = setFormUnitSystem(nextFormUnitSystem);
    onFormUnitSystemChange?.(nextFormUnitSystem, convertedLifetimeParameters);
  };

  // map trs config properties to the ones used by IBD
  const defaultLifetimeFormConfig = {
    ...trsConfig,
    offTime: trsConfig.intermittentOffTime,
    onTime: trsConfig.intermittentOnTime,
    oppositeMaterialId: trsConfig.oppositeMaterial,
    [lifetimeConfigParams.force]: {
      ...trsConfig.force,
      min: 1 / ROUNDING_PRECISION,
    },
    [lifetimeConfigParams.rotatingSpeed]: {
      min: 0.000001,
      max: 100,
    },
    [lifetimeConfigParams.linearSpeed]: {
      min: 0.000001,
      max: 100,
    },
    [lifetimeConfigParams.pivotingSpeed]: {
      min: 0.000001,
      max: 100,
    },
  };

  const inputsDefinitions = useMemo(
    () =>
      createInputsDefinitions({
        t,
        unitSystem: formUnitSystem,
        locale,
        getSurfacePressureValue: getSurfacePressureValueForDefinitions,
        getRotatingSpeedValue,
        getLinearSpeedValue,
        getPivotingSpeedValue,
      }),
    [
      t,
      formUnitSystem,
      locale,
      getSurfacePressureValueForDefinitions,
      getRotatingSpeedValue,
      getLinearSpeedValue,
      getPivotingSpeedValue,
    ]
  );

  const getLifetimeParameterType = (
    type: LifetimeParameterInputSectionType
  ): LifetimeConfigTypeOfOperation | SpatialTypeOfMovement | 'default' => {
    let currentItemType: LifetimeConfigTypeOfOperation | SpatialTypeOfMovement | 'default' = 'default';
    if (type === LifetimeParameterInputSectionType.operation) {
      currentItemType = lifetimeParameters.typeOfOperation;
    } else if (type === LifetimeParameterInputSectionType.motion) {
      currentItemType = lifetimeParameters.typeOfMovement;
    }
    return currentItemType;
  };

  useEffect(() => {
    const inputs = inputsDefinitions.flatMap(({ items, type }) =>
      items[getLifetimeParameterType(type)].filter(item => {
        if (item.attribute in visibilityMap) {
          return visibilityMap[item.attribute];
        }

        return !item.readOnly && item.inputType === LifetimeParameterInputType.numberInput;
      })
    );

    const valid = inputs.every(item => {
      const attributeConfig = defaultLifetimeFormConfig?.[item.attribute] as LifetimeInputFieldConfig;
      const value = lifetimeParameters?.[item.attribute] as string;

      return validateInput(value, attributeConfig).isValid;
    });
    setFormValidation(valid);
  }, [lifetimeParameters, defaultLifetimeFormConfig, inputsDefinitions, visibilityMap, validateInput]);

  const [expanded, setExpanded] = useState(isEmbedded || false);

  const hintModal = (
    <Modal variant={ModalVariant.ALERT} visible={hintMessage !== undefined} onClose={() => setHintMessage(undefined)}>
      <div className="p-8 overflow-auto">
        <h3 data-testId="life-Time-Modal_Header">{t(hintMessage?.title, { ns: 'bearing-hub/bearingHub' })}</h3>
        <div data-testid="life-time-modal-content" className="flex flex-col md:flex-row gap-4 items-center mt-6">
          {hintMessage?.image && (
            <div className="flex-shrink-0">
              <img src={hintMessage.image} alt="Lifetime hint" className="w-auto h-auto max-w-[200px]" />
            </div>
          )}
          <div className="flex-1">{hintMessage?.message}</div>
        </div>
      </div>
    </Modal>
  );

  return (
    <div className={isEmbedded ? 'mt-6' : ''} data-testid="life-time-parameters-form">
      {hintModal}
      {!isEmbedded && (
        <div className="w-full mb-6">
          <button
            className="text-igus-primary"
            type="button"
            onClick={() => {
              const eventAction = expanded
                ? TrackedIbhEventActions.applicationParametersCollapse
                : TrackedIbhEventActions.applicationParametersExpand;
              CustomEvent.trackEvent(IbhEventCategory, eventAction);
              setExpanded(!expanded);
            }}
          >
            <span className="mr-2">{t(`LIFETIME_PARAMETERS_LINK`, { ns: 'bearing-hub/bearingHub' })}</span>
            <div className="-rotate-90 inline-block">
              <Icon size={10} color={IconColor.ORANGE} id={expanded ? IconID.CHEVRON_RIGHT : IconID.CHEVRON_LEFT} />
            </div>
          </button>
        </div>
      )}
      <div
        className={`lg:mx-6 overflow-hidden transition-max-height duration-250 ease-in-out ${
          expanded ? 'max-h-[100%]' : 'max-h-[0px]'
        }`}
      >
        <Switch
          checked={formUnitSystem === UnitSystem.imperial}
          optionOne={t('COMMON.METRIC_TYPE.METRIC', { ns: 'bearing-hub/bearingHub' })}
          optionTwo={t('COMMON.METRIC_TYPE.IMPERIAL', { ns: 'bearing-hub/bearingHub' })}
          onChange={handleFormUnitSystemChange}
          theme="secondary"
          customStyles="mb-2"
        />
        <div className="mb-8">
          <Icon size={6} color={IconColor.BLUE} id={IconID.INFO} />
          <span className="text-xs leading-8">{t(`UNIT_SYSTEM_TOGGLE_INFO`, { ns: 'bearing-hub/bearingHub' })}</span>
        </div>
        <form id="form">
          {inputsDefinitions.map(inputsDefinition => {
            const currentItemType = getLifetimeParameterType(inputsDefinition.type);

            let buttonGroupElem = null;
            if (currentItemType !== 'default') {
              if (inputsDefinition.type === LifetimeParameterInputSectionType.operation) {
                const operationOptions = [
                  {
                    text: t('LIFETIME.CONTINOUS', { ns: 'bearing-hub/bearingHub' }),
                    value: lifetimeConfigTypeOfOperation.continous,
                  },
                  {
                    text: t('LIFETIME.INTERMITTENT', { ns: 'bearing-hub/bearingHub' }),
                    value: lifetimeConfigTypeOfOperation.intermittent,
                    isDisabled: true,
                  },
                ];
                buttonGroupElem = (
                  <div className="mb-6" data-testid="lifetime_parameter_typeOfOperation">
                    <ButtonGroup<LifetimeConfigTypeOfOperation>
                      buttonGroupDefinition={operationOptions}
                      onButtonClick={(value: LifetimeConfigTypeOfOperation) => {
                        onLifetimeParameterValueChange(lifetimeConfigParams.typeOfOperation, value);
                      }}
                      selectedButton={operationOptions.findIndex(
                        item => item.value === lifetimeParameters.typeOfOperation
                      )}
                    />
                  </div>
                );
              } else if (inputsDefinition.type === LifetimeParameterInputSectionType.motion) {
                const motionOptions = [
                  {
                    text: t('LIFETIME.ROTATION', { ns: 'bearing-hub/bearingHub' }),
                    value: spatialTypeOfMovement.rotation,
                  },
                  {
                    text: t('LIFETIME.LINEAR', { ns: 'bearing-hub/bearingHub' }),
                    value: spatialTypeOfMovement.linear,
                  },
                  {
                    text: t('LIFETIME.PIVOTING', { ns: 'bearing-hub/bearingHub' }),
                    value: spatialTypeOfMovement.pivoting,
                  },
                  {
                    text: t('LIFETIME.LINEAR_AND_ROTATION', { ns: 'bearing-hub/bearingHub' }),
                    value: spatialTypeOfMovement.linearAndRotation,
                  },
                  {
                    text: t('LIFETIME.LINEAR_AND_PIVOTING', { ns: 'bearing-hub/bearingHub' }),
                    value: spatialTypeOfMovement.linearAndPivoting,
                  },
                ];

                buttonGroupElem = (
                  <div className="mb-6" data-testid="lifetime_parameter_typeOfMovement">
                    <ButtonGroup<SpatialTypeOfMovement>
                      buttonGroupDefinition={motionOptions}
                      onButtonClick={(value: SpatialTypeOfMovement) => {
                        onLifetimeParameterValueChange(lifetimeConfigParams.typeOfMovement, value);
                      }}
                      selectedButton={motionOptions.findIndex(item => item.value === lifetimeParameters.typeOfMovement)}
                    />
                  </div>
                );
              }
            }

            return (
              <div className="flex flex-col" key={inputsDefinition.title}>
                <div className="font-bold mb-6">{t(inputsDefinition.title, { ns: 'bearing-hub/bearingHub' })}</div>
                {buttonGroupElem}
                <div className="flex flex-row items-start lg:items-end lg:flex-wrap justify-between lg:justify-start flex-wrap-reverse mb-12">
                  {inputsDefinition.items[currentItemType].map(item => {
                    const displayedValue =
                      typeof item.defaultValue === 'number'
                        ? numberFormatter.format(item.defaultValue)
                        : item.defaultValue;
                    let inputElem = (
                      <div
                        data-testid={`lifetime-read-only-${item.attribute}`}
                        className="font-bold min-h-[40px] pt-2 text-[#666666]"
                      >
                        {displayedValue} {item.unit}
                      </div>
                    );
                    const showInput = visibilityMap[item.attribute] ?? true;
                    if (!item.readOnly && item.inputType === LifetimeParameterInputType.numberInput) {
                      const attributeConfig = defaultLifetimeFormConfig?.[item.attribute] as LifetimeInputFieldConfig;
                      const value = lifetimeParameters[item.attribute] as string | number;
                      const { isValid: validInput, min, max } = validateInput(value, attributeConfig);
                      inputElem = (
                        <div className={validInput ? '' : '[&_input]:border-red-500'}>
                          <BasicNumberInput
                            id={`filter_${item.attribute}`}
                            unit={item.unit}
                            value={value == null || value === '' ? '' : round4(Number(value)).toString()}
                            unitWidth={20}
                            onBlur={() => {}}
                            onChange={userValue => {
                              onLifetimeParameterValueChange(item.attribute, userValue || null);
                            }}
                          />
                          {!validInput && (
                            <p className="sm:absolute text-xs text-red-500">
                              {t('LIFETIME.PARAMETER_BETWEEN_MIN_MAX', {
                                ns: 'bearing-hub/bearingHub',
                                minValue: `${min} ${item.unit || ''}`,
                                maxValue: `${max} ${item.unit || ''}`,
                              })}
                            </p>
                          )}
                        </div>
                      );
                    } else if (
                      !item.readOnly &&
                      item.inputType === LifetimeParameterInputType.filterSelectAutocomplete
                    ) {
                      inputElem = (
                        <FilterSelectAutocomplete
                          isClearable={false}
                          id={`select_filter_${item.attribute}`}
                          placeholder={t(item.attributeText, { ns: 'bearing-hub/bearingHub' })}
                          hideOptionsModel
                          options={Object.keys(OppositeMaterialMap).map(key => ({
                            label: t(`SHAFT_MATERIAL.${OppositeMaterialMap[key as OppositeMaterialKeyType]}`, {
                              ns: 'bearing-hub/bearingHub',
                            }),
                            value: key,
                          }))}
                          value={{
                            label: t(
                              `SHAFT_MATERIAL.${
                                OppositeMaterialMap[
                                  (lifetimeParameters[item.attribute] || item.defaultValue) as OppositeMaterialKeyType
                                ]
                              }`,
                              {
                                ns: 'bearing-hub/bearingHub',
                              }
                            ),
                            value: (lifetimeParameters[item.attribute] || item.defaultValue) as string,
                          }}
                          onChange={value => {
                            onLifetimeParameterValueChange(item.attribute, value as string);
                          }}
                        />
                      );
                    } else if (!item.readOnly && item.inputType === LifetimeParameterInputType.buttonGroup) {
                      inputElem = (
                        <ButtonGroup<TemporalTypeOfMovement>
                          buttonGroupDefinition={item.buttonGroupDefinition}
                          onButtonClick={(value: TemporalTypeOfMovement) =>
                            onLifetimeParameterValueChange(lifetimeConfigParams.temporalTypeOfMovement, value)
                          }
                          selectedButton={item.buttonGroupDefinition.findIndex(
                            el => el.value === lifetimeParameters.temporalTypeOfMovement
                          )}
                        />
                      );
                    }
                    return (
                      showInput && (
                        <div
                          key={item.attribute}
                          className={`relative mr-6 ${
                            item.inputType !== LifetimeParameterInputType.buttonGroup ? 'w-[150px]' : 'w-full mb-6'
                          }`}
                          data-testid={`lifetime-parameter-input-${item.attribute}`}
                        >
                          <label
                            id={`filter_label_${item.attribute}`}
                            htmlFor={`filter_${item.attribute}`}
                            className="block mb-2 text-sm	"
                          >
                            {t(item.title, { ns: 'bearing-hub/bearingHub' })}
                            {item.hint && (
                              <span
                                className="ml-2 cursor-pointer"
                                data-testid="show-life-time-modal-hint"
                                onClick={event => {
                                  event.stopPropagation();
                                  setHintMessage({
                                    title: item.title,
                                    message: item.hint,
                                    image: item.hintImage,
                                  });
                                }}
                              >
                                <Icon size={6} id={IconID.INFO_CIRCLE_GRAY} />
                              </span>
                            )}
                          </label>
                          {inputElem}
                        </div>
                      )
                    );
                  })}
                </div>
              </div>
            );
          })}
        </form>
        <div className="flex w-full flex-col lg:flex-row justify-end mb-6 items-start lg:items-center">
          {hasLifetimeParameterValues() && (
            <div className="lg:w-[240px]">
              <FilterResetLink
                text={t('LIFETIME.RESTORE_DEFAULT_VALUES', { ns: 'bearing-hub/bearingHub' })}
                dataTestId="remove_all_parameters"
                reset={onReset}
              />
            </div>
          )}
          <div className="lg:w-[240px] w-full">
            <Button
              className={isFormValid ? 'bg-igus-primary w-full' : 'cursor-not-allowed w-full'}
              disabled={!isFormValid}
              icon={<Icon size={10} color={IconColor.WHITE} id={IconID.LIFETIME_CALCULATOR} />}
              text={t('LIFETIME.CALCUALTE', { ns: 'bearing-hub/bearingHub' })}
              type={TYPE.SECONDARY}
              loaderText={t('COMMON.SELECTION_LOADING', { ns: 'bearing-hub/bearingHub' })}
              loading={lifetimeIsLoading}
              onClick={() => {
                CustomEvent.trackEvent(IbhEventCategory, TrackedIbhEventActions.applicationParametersCalculate);
                setTableHeadersSortingState('lifetime', SortingState.Descending);
                scrollToElement('product-table');
                onCalculate();
              }}
              testId="calculate-lifetime-button"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
